import { ThemeProvider as MUIThemeProvider, createTheme } from "@mui/material";
import {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useState,
  useEffect
} from "react";

type ThemeMode = "light" | "dark";

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  /** The home page easter egg: type "techno" there. */
  techno: boolean;
  toggleTechno: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within ThemeProvider");
  }
  return context;
};

declare module "@mui/material/styles" {
  interface Palette {
    MFT_ORANGE: "#FF8C3D";
    MFT_DARK_BLUE: "#093952";
    MFT_LIGHT_BLUE: "#BCE1FF";
    MFT_OFF_WHITE: "#F1F6F8";
    MFT_CREAM: "#DFD0BF";
    MFT_GREEN: "#8ACEBA";
    NICE_GREEN: "#66D575";
    NICE_ORANGE: "#FEC843";
  }
  interface PaletteOptions {
    MFT_ORANGE: "#FF8C3D";
    MFT_DARK_BLUE: "#093952";
    MFT_LIGHT_BLUE: "#BCE1FF";
    MFT_OFF_WHITE: "#F1F6F8";
    MFT_CREAM: "#DFD0BF";
    MFT_GREEN: "#8ACEBA";
    NICE_GREEN: "#66D575";
    NICE_ORANGE: "#FEC843";
  }
}

declare module "@mui/material/Typography" {
  export interface TypographyPropsColorOverrides {
    MFT_ORANGE: true;
    MFT_DARK_BLUE: true;
    MFT_LIGHT_BLUE: true;
    MFT_OFF_WHITE: true;
    MFT_CREAM: true;
    MFT_GREEN: true;
    NICE_GREEN: true;
    NICE_ORANGE: true;
  }
}

declare module "@mui/material/SvgIcon" {
  export interface SvgIconPropsColorOverrides {
    MFT_ORANGE: true;
    MFT_DARK_BLUE: true;
    MFT_LIGHT_BLUE: true;
    MFT_OFF_WHITE: true;
    MFT_CREAM: true;
    MFT_GREEN: true;
    NICE_GREEN: true;
    NICE_ORANGE: true;
  }
}

export const MFT_COLORS = {
  MFT_ORANGE: "#FF8C3D",
  MFT_DARK_BLUE: "#093952",
  MFT_LIGHT_BLUE: "#BCE1FF",
  MFT_OFF_WHITE: "#F1F6F8",
  MFT_CREAM: "#DFD0BF",
  MFT_GREEN: "#8ACEBA",
  NICE_GREEN: "#66D575",
  NICE_ORANGE: "#FEC843"
};

export const lightTheme = createTheme({
  components: {
    MuiTypography: {
      defaultProps: {
        component: "span",
        color: "textPrimary"
      }
    }
  },
  typography: {
    h6: {
      fontFamily: "Europa"
    },
    h5: {
      fontFamily: "Europa"
    }
  },
  palette: {
    mode: "light",
    MFT_ORANGE: "#FF8C3D",
    MFT_DARK_BLUE: "#093952",
    MFT_LIGHT_BLUE: "#BCE1FF",
    MFT_OFF_WHITE: "#F1F6F8",
    MFT_CREAM: "#DFD0BF",
    MFT_GREEN: "#8ACEBA",
    NICE_GREEN: "#66D575",
    NICE_ORANGE: "#FEC843",
    primary: {
      main: "#093952"
    },
    secondary: {
      main: "#FF8C3D"
    },
    success: {
      main: "#8aceba"
    },
    info: {
      main: "#58a6d4"
    },
    background: {
      default: "#f1f6f8"
    }
  }
});

/**
 * Dark mode surfaces, a slate ladder with a faint cool tint (a full MFT_DARK_BLUE navy read as
 * too blue, the old neutral greys blended together). The app
 * frame (topbar, nav) is the darkest step so the content comes forward, then each layer that
 * sits on another is one step lighter: page, card, section inside a card, menus and dialogs.
 * Paper picks its step from its variant and elevation below, so nested cards stop blending.
 */
export const darkSurfaces = {
  chrome: "#0a0f13",
  canvas: "#0f151a",
  header: "#141c22",
  surface: "#172027",
  raised: "#1d2830",
  overlay: "#232f38",
  border: "rgba(205, 218, 228, 0.09)",
  borderStrong: "rgba(205, 218, 228, 0.16)",
  highlight: "inset 0 1px 0 rgba(255, 255, 255, 0.04)"
};

type DarkSurfaces = typeof darkSurfaces;

type DarkAccents = {
  primary: { main: string; contrastText: string };
  secondary: { main: string };
  text: { primary: string; secondary: string };
};

const darkPaperBackground = (
  s: DarkSurfaces,
  variant?: string,
  elevation = 1
) => {
  if (variant === "outlined") return s.raised;
  if (elevation > 6) return s.overlay;
  return s.surface;
};

/**
 * Builds a dark theme from a surface ladder and accents. Dark mode and the techno easter egg
 * share every component override this way and differ only in colour.
 */
const createDarkTheme = (s: DarkSurfaces, accents: DarkAccents) =>
  createTheme({
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: s.canvas
          },
          // Navy scrollbar thumbs; Chrome's default dark thumb is a light grey bar on this palette.
          "*": {
            scrollbarColor: `${s.overlay} transparent`
          }
        }
      },
      MuiTypography: {
        defaultProps: {
          component: "span",
          color: "textPrimary"
        }
      },
      MuiTextField: {
        defaultProps: {
          color: "secondary"
        }
      },
      MuiInput: {
        defaultProps: {
          color: "secondary"
        }
      },
      MuiFilledInput: {
        defaultProps: {
          color: "secondary"
        }
      },
      MuiInputLabel: {
        defaultProps: {
          color: "secondary"
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            backgroundImage: "none",
            backgroundColor: darkPaperBackground(
              s,
              ownerState.variant,
              ownerState.elevation
            )
          }),
          outlined: {
            borderColor: s.border
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${s.border}`,
            boxShadow: `${s.highlight}, 0 10px 30px -18px rgba(0, 0, 0, 0.9)`
          }
        }
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            border: `1px solid ${s.borderStrong}`
          }
        }
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            border: `1px solid ${s.borderStrong}`
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            border: `1px solid ${s.borderStrong}`,
            boxShadow: `${s.highlight}, 0 24px 60px -12px rgba(0, 0, 0, 0.8)`
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: s.overlay,
            border: `1px solid ${s.borderStrong}`
          },
          arrow: {
            color: s.overlay
          }
        }
      },
      // Fields sit slightly recessed into the card they are on, so a form reads as inputs rather
      // than as outlines drawn on the same flat colour.
      MuiOutlinedInput: {
        defaultProps: {
          color: "secondary"
        },
        styleOverrides: {
          root: {
            backgroundColor: "rgba(5, 8, 11, 0.35)"
          },
          notchedOutline: {
            borderColor: s.borderStrong
          }
        }
      }
    },

    typography: {
      h6: {
        fontFamily: "Europa"
      },
      h5: {
        fontFamily: "Europa"
      }
    },
    palette: {
      MFT_ORANGE: "#FF8C3D",
      MFT_DARK_BLUE: "#093952",
      MFT_LIGHT_BLUE: "#BCE1FF",
      MFT_OFF_WHITE: "#F1F6F8",
      MFT_CREAM: "#DFD0BF",
      MFT_GREEN: "#8ACEBA",
      NICE_GREEN: "#66D575",
      NICE_ORANGE: "#FEC843",
      mode: "dark",
      divider: s.border,
      secondary: accents.secondary,
      primary: accents.primary,
      text: accents.text,
      success: {
        main: "#8aceba"
      },
      info: {
        main: "#58a6d4"
      },
      background: {
        default: s.canvas,
        paper: s.surface
      }
    }
  });

export const darkTheme = createDarkTheme(darkSurfaces, {
  secondary: { main: "#ff8c3d" },
  // MFT light blue with navy text: crisp on the dark surfaces, where the old mid sky blue
  // washed out.
  primary: { main: "#BCE1FF", contrastText: "#062233" },
  text: { primary: "#F1F6F8", secondary: "#a4afb7" }
});

/**
 * Techno mode (the home page easter egg, see components/technoMode): an ultraviolet warehouse
 * ladder with neon cyan and hot magenta. The glow, the mirror ball and the beat live in the
 * technoMode component; this is only the colour.
 */
export const technoSurfaces: DarkSurfaces = {
  chrome: "#06020d",
  canvas: "#0b0416",
  header: "#120725",
  surface: "#170a2c",
  raised: "#211139",
  overlay: "#2a1647",
  border: "rgba(255, 43, 214, 0.28)",
  borderStrong: "rgba(0, 240, 255, 0.35)",
  highlight: "inset 0 1px 0 rgba(255, 255, 255, 0.06)"
};

export const TECHNO_COLORS = {
  cyan: "#00F0FF",
  magenta: "#FF2BD6",
  violet: "#8B3DFF",
  acid: "#C6FF3D"
};

export const technoTheme = createDarkTheme(technoSurfaces, {
  primary: { main: TECHNO_COLORS.cyan, contrastText: "#0b0416" },
  secondary: { main: TECHNO_COLORS.magenta },
  text: { primary: "#F7F0FF", secondary: "#c9aef0" }
});

export const ThemeProvider: FC<PropsWithChildren> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem("themeMode");
    return savedMode === "dark" || savedMode === "light" ? savedMode : "light";
  });

  useEffect(() => {
    localStorage.setItem("themeMode", mode);
  }, [mode]);

  // Techno mode sits on top of light/dark rather than replacing it, and is deliberately not
  // persisted: a reload always brings back the normal app.
  const [techno, setTechno] = useState(false);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  const toggleTechno = () => setTechno((prev) => !prev);

  const theme = techno
    ? technoTheme
    : mode === "light"
      ? lightTheme
      : darkTheme;

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, techno, toggleTechno }}>
      <MUIThemeProvider theme={theme}>{children}</MUIThemeProvider>
    </ThemeContext.Provider>
  );
};

export enum Colors {
  MFT_ORANGE = "#FF8C3D",
  MFT_DARK_BLUE = "#093952",
  MFT_LIGHT_BLUE = "#BCE1FF",
  MFT_OFF_WHITE = "#F1F6F8",
  MFT_CREAM = "#DFD0BF",
  MFT_GREEN = "#8ACEBA",
  NICE_GREEN = "#66D575",
  NICE_ORANGE = "#FEC843"
}
